import type { LucideIcon } from 'lucide-react';
import { Boxes, LayoutGrid, Palette, ScrollText, User, Users } from 'lucide-react';
import type { Me } from '../api';

/** Something that can be launched from the desktop: a registered app (external URL) or an OS page (route). */
export type DesktopItem = {
  id: string;
  name: string;
  description?: string;
  kind: 'app' | 'system';
  /** Registered apps open in the same tab via their URL. */
  href?: string;
  /** OS pages are client-side routes. */
  to?: string;
  /** Two-letter mark for app icons. */
  mark?: string;
  icon?: LucideIcon;
  roles?: string[];
};

export const SYSTEM_ITEMS: Array<DesktopItem & { to: string; admin?: boolean }> = [
  { id: 'launcher', name: 'Launcher', description: 'All your apps', kind: 'system', to: '/', icon: LayoutGrid },
  { id: 'account', name: 'Account', description: 'Profile, password, security', kind: 'system', to: '/account', icon: User },
  { id: 'admin-users', name: 'Users & roles', description: 'People and app access', kind: 'system', to: '/admin/users', icon: Users, admin: true },
  { id: 'admin-apps', name: 'Registered apps', description: 'App registry', kind: 'system', to: '/admin/apps', icon: Boxes, admin: true },
  { id: 'admin-audit', name: 'Audit log', description: 'Who did what', kind: 'system', to: '/admin/audit', icon: ScrollText, admin: true },
  { id: 'dev-ui', name: 'UI gallery', description: '@os/ui components', kind: 'system', to: '/dev/ui', icon: Palette, admin: true },
];

export function systemItems(isAdmin: boolean): DesktopItem[] {
  return SYSTEM_ITEMS.filter((i) => !i.admin || isAdmin);
}

export function appItems(me: Me | undefined): DesktopItem[] {
  return (me?.apps ?? []).map((a) => ({ id: a.id, name: a.name, description: a.description ?? undefined, kind: 'app', href: a.url, mark: a.icon ?? a.name.slice(0, 2), roles: a.roles }));
}

/** Title shown in the menu bar / window chrome for a route. */
export function titleFor(pathname: string): string {
  return SYSTEM_ITEMS.find((i) => i.to === pathname)?.name ?? 'OS';
}

/** Stable hue per app id so every app gets its own icon colour. */
export function iconBackground(id: string): string {
  let h = 0;
  for (const ch of id) h = (h * 31 + ch.charCodeAt(0)) >>> 0;
  const hue = h % 360;
  return `linear-gradient(145deg, hsl(${hue} 72% 58%), hsl(${(hue + 32) % 360} 70% 42%))`;
}

export function initials(name: string): string {
  return name.split(/\s+/).map((w) => w[0]).join('').slice(0, 2).toUpperCase() || '?';
}
